import { useCampaignApproval } from 'hooks/useCampaignApproval'
import moment from 'moment'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiCallOut, EuiSpacer } from '@elastic/eui'

import { Campaign } from 'api/interfaces'
import { useSubmitCampaignForApprovalMutation } from 'api/rtkQueryApi/platform/campaignsApi'
import { showErrorToast, showSuccessToast } from 'app/appSlice'
import { RootState } from 'app/rootReducer'
import { fetchCampaigns } from 'features/campaigns/campaigns/campaignsSlice'

import { fetchCampaign } from './campaignSlice'

interface CampaignApprovalCallOutProps {
  campaign: Campaign
}

const formatDecisionDate = (date: moment.Moment | null) => (date ? ` on ${moment(date).format('MMM D, YYYY h:mm A')}` : '')

export const CampaignApprovalCallOut: React.FC<CampaignApprovalCallOutProps> = ({ campaign }) => {
  const dispatch = useDispatch()
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const { isEnabled: campaignApprovalEnabled, canCreate } = useCampaignApproval()
  const [submitCampaignForApproval, submitCampaignForApprovalResult] = useSubmitCampaignForApprovalMutation()

  const onSubmitForApproval = async () => {
    if (!currentAccount) return
    try {
      await submitCampaignForApproval({ accountId: currentAccount.id, campaignId: campaign.id }).unwrap()
      dispatch(showSuccessToast('Campaign submitted for approval'))
      dispatch(fetchCampaign(currentAccount.id, campaign.id))
      dispatch(fetchCampaigns(currentAccount.id))
    } catch {
      dispatch(showErrorToast('Failed to submit campaign for approval'))
    }
  }

  if (campaign.disapprovedBy) {
    return (
      <React.Fragment>
        <EuiCallOut title={`Disapproved by ${campaign.disapprovedByName ?? campaign.disapprovedBy}${formatDecisionDate(campaign.disapproved)}`} iconType='alert' color='danger'>
          {campaign.disapprovedReason && <p>{campaign.disapprovedReason}</p>}
          {campaignApprovalEnabled && canCreate && (
            <EuiButton onClick={onSubmitForApproval} isLoading={submitCampaignForApprovalResult.isLoading}>
              Submit for approval
            </EuiButton>
          )}
        </EuiCallOut>
        <EuiSpacer />
      </React.Fragment>
    )
  }

  if (campaign.approvedBy) {
    return (
      <React.Fragment>
        <EuiCallOut title={`Approved by ${campaign.approvedByName ?? campaign.approvedBy}${formatDecisionDate(campaign.approved)}`} iconType='check' color='success' />
        <EuiSpacer />
      </React.Fragment>
    )
  }

  return null
}
